import {
  HHButton,
  HHDivider,
  HHGrid,
  HHLoadingButton,
  HHSelect,
  HHSelectChangeEvent,
} from '@hinge-health/react-component-library';
import { Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { HingeConnectAutocomplete } from '../../components/hinge-connect-autocomplete';
import { NoRowsMessage } from '../../components/no-rows';
import { buttonLabels } from '../../constants/hinge-connect-constants.constant';
import { useListTagNames } from '../../user-tags/hooks/list-tag-names-hook';
import { useSupportedActions } from '../hooks/use-supported-actions';
import {
  DynamicActionForm,
  FieldOverrideRenderer,
  TagArrayField,
  TaggedItem,
} from './actions/dynamic-action-form';

export const CreateActionForm = (props: {
  onSubmit: (data: { type: string; metadata: object }) => void;
  onCancel: () => void;
}): JSX.Element => {
  const [action, setAction] = useState('');
  const [metadata, setMetaData] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const { actions, loading, error } = useSupportedActions();

  // Only fetch tag names when the selected action requires them
  const needsTagNames =
    action === 'CREATE_USER_TAG' || action === 'EMIT_BACKFILL_USER_TAG_EVENT';
  const tagNames = useListTagNames({
    cacheValidationDuration: 60 * 1000,
    skip: !needsTagNames,
  });

  const tagOptions = useMemo(
    () => tagNames.map(t => ({ label: t, value: t })),
    [tagNames],
  );
  const selectOptions = useMemo(
    () => actions.map(a => ({ value: a.type, displayName: a.name })),
    [actions],
  );
  const selectedAction = useMemo(
    () => actions.find(a => a.type === action),
    [actions, action],
  );

  const tagAutocomplete = useCallback(
    (
      value: unknown,
      onChange: (value: unknown) => void,
      label: string,
      clearOnBlur = false,
    ): JSX.Element => (
      <HingeConnectAutocomplete
        label={label}
        value={(value as string) ?? ''}
        dropdownOptions={tagOptions}
        required
        clearOnBlur={clearOnBlur}
        onChange={(v): void => onChange(v ?? '')}
      />
    ),
    [tagOptions],
  );

  const fieldOverrides = useMemo(():
    | Record<string, FieldOverrideRenderer>
    | undefined => {
    if (action === 'CREATE_USER_TAG') {
      return {
        tagName: (value, onChange): JSX.Element =>
          tagAutocomplete(value, onChange, 'tagName', true),
      };
    }
    if (action === 'EMIT_BACKFILL_USER_TAG_EVENT') {
      return {
        tagsToEmit: (value, onChange): JSX.Element => {
          const items = (value as TaggedItem[]) ?? [];
          return (
            <TagArrayField
              label="tagsToEmit *"
              items={items}
              onChange={onChange as (items: TaggedItem[]) => void}
              renderItem={(item, index, onItemChange): JSX.Element =>
                tagAutocomplete(
                  item.value,
                  (v): void => onItemChange((v as string) ?? ''),
                  `tag ${index + 1}`,
                  true,
                )
              }
              addTestId="create-action-tags-to-emit-add-item-button"
              itemLabel="tag"
            />
          );
        },
      };
    }
    return undefined;
  }, [action, tagAutocomplete]);

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    props.onSubmit({ type: action, metadata });
  };

  const handleActionSelect = (e: HHSelectChangeEvent): void => {
    setAction(e.target.value as string);
  };

  return (
    <form onSubmit={handleSubmit}>
      <HHGrid container spacing={2}>
        <HHGrid item xs={3}>
          <HHSelect
            fullWidth
            disabled={loading}
            selectOptions={selectOptions}
            initialSelectValue={action}
            label={loading ? 'Loading actions...' : 'Action'}
            onChange={(e): void => handleActionSelect(e)}
          ></HHSelect>
        </HHGrid>
        <HHGrid item xs={8}></HHGrid>
        <HHGrid item xs={12}>
          <HHDivider orientation="horizontal" />
        </HHGrid>
        {selectedAction && (
          <HHGrid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              {selectedAction.description}
            </Typography>
            {selectedAction.documentation?.usageNotes && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Note: {selectedAction.documentation.usageNotes}
              </Typography>
            )}
            {(selectedAction.documentation?.relatedActions?.length ?? 0) >
              0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Related actions:{' '}
                {selectedAction.documentation?.relatedActions?.join(', ')}
              </Typography>
            )}
          </HHGrid>
        )}
        <HHGrid item xs={12} paddingTop="1rem">
          {selectedAction && (
            <DynamicActionForm
              schema={selectedAction.metadata.schema}
              example={selectedAction.metadata.example}
              onChange={setMetaData}
              onIsValid={setIsFormValid}
              fieldOverrides={fieldOverrides}
            />
          )}
        </HHGrid>
        <HHGrid item xs={12}>
          {error && (
            <NoRowsMessage
              title={'Failed to load actions'}
              body={
                'There was a problem loading the available actions. Please try again.'
              }
            />
          )}
          {!error && !action && (
            <NoRowsMessage
              title={'Select an Action'}
              body={'Select an action from the dropdown to continue.'}
            />
          )}
        </HHGrid>
      </HHGrid>
      <HHGrid
        container
        display={'flex'}
        spacing={2}
        justifyContent={'end'}
        marginTop={'1rem'}
      >
        <HHGrid item xs={8} />
        <HHGrid item xs={0}>
          <HHButton
            data-testid="cancel-action-button"
            hhVariant="variant-bypass"
            variant="text"
            sx={{ width: 'fit-content' }}
            onClick={props.onCancel}
          >
            {buttonLabels.cancel}
          </HHButton>
        </HHGrid>
        <HHGrid item xs={0}>
          <HHLoadingButton
            hhVariant="variant-bypass"
            variant="contained"
            sx={{ width: 'fit-content' }}
            type="submit"
            loading={false}
            disabled={!isFormValid}
            data-testid="submit-button"
          >
            Add
          </HHLoadingButton>
        </HHGrid>
      </HHGrid>
    </form>
  );
};
